import { faListCheck } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  BoxV2,
  ButtonV2,
  LabraToolTipV2,
  PlanType,
  RadioButtonsV2,
  TextInputV2,
} from '@labrav/react-components'
import { FormikProps, useFormikContext } from 'formik'
import { get, isEmpty } from 'lodash'
import { useStyles } from './OfferMarkup.styles'
import { useStyles as useCommonStyles } from '../AddEditChannelOfferComponents.styles'
import {
  ChannelPartnerPrivateOfferInCreation,
  CppoPriceAdjustmentType,
} from '../../../../modules/channelPartnerPrivateOffer/reducer'
import { isNonEmptyNumber, numberFormatWithPrecision } from '@labrav/common'
import clsx from 'clsx'
import {
  getMarkupValues,
  getMarkupOptions,
} from '../../../../utils/channelOfferUtil'
import { DiscountType } from '../../../../types/flyout'
import type { ReceivedResellerAuthorization } from '../../../../modules/receivedResellerAuthorization/reducer'
import type { PriceTypes } from '../../../../modules/privateOffer/reducer'
import { TotalComponent } from './TotalComponent'
import { calculateBuyerPrice } from './helpers'

export const OfferMarkup = ({
  productType,
  discountType,
  resaleAuthorization,
  shouldShowMultiple = true,
  showTcv,
  isUpdateIsvPayment,
}: {
  productType: PlanType
  discountType?: DiscountType
  resaleAuthorization?: ReceivedResellerAuthorization
  shouldShowMultiple?: boolean
  showTcv?: boolean
  isUpdateIsvPayment?: boolean
}) => {
  const { classes } = useStyles()
  const { classes: commonClasses } = useCommonStyles()
  const formikProps =
    useFormikContext() as FormikProps<ChannelPartnerPrivateOfferInCreation>
  const {
    touched,
    errors,
    values,
    handleBlur,
    setFieldTouched,
    setFieldValue,
    setFieldError,
  } = formikProps

  const getErrorMsg = (keyName: string) => {
    return ((!!get(touched, keyName, false) && get(errors, keyName, ' ')) ||
      '') as string
  }

  const applyAdjustmentPercentage = async () => {
    setFieldValue(
      'adjustmentPercentage',
      values?.adjustmentPercentageInputValue
    )
    if (values?.flexiblePaymentTerms) {
      const installmentsData = values?.installments || []
      const selectedValue = values?.adjustmentPercentageInputValue
      if (values?.priceAdjustmentType === CppoPriceAdjustmentType.FIXED) {
        const amountValue = parseFloat(
          (Number(selectedValue!) / installmentsData.length).toFixed(2)
        )
        const updatedInstallments = installmentsData.map(item => ({
          ...item,
          amount: amountValue,
        }))
        await setFieldValue('installments', updatedInstallments)
      } else {
        const { markupOrTotal, totalDueToIsv } = getMarkupValues({
          dimensions: values?.dimensions,
          adjustmentPercentage: values?.adjustmentPercentageInputValue,
          priceAdjustmentType: values?.priceAdjustmentType,
          resaleAuthorization,
        })
        const updatedInstallments = installmentsData.map(item => ({
          ...item,
          amount: parseFloat(
            (Number(markupOrTotal!) / installmentsData.length).toFixed(2)
          ),
          ...(isUpdateIsvPayment
            ? {
                otherAmount: isNonEmptyNumber(totalDueToIsv)
                  ? parseFloat(
                      (totalDueToIsv! / installmentsData.length).toFixed(2)
                    )
                  : '-',
              }
            : {}),
        }))
        await setFieldValue('installments', updatedInstallments)
      }
    }

    if (isNonEmptyNumber(values?.adjustmentPercentageInputValue)) {
      const { markupOrTotal } = getMarkupValues({
        dimensions: values?.dimensions,
        adjustmentPercentage: values?.adjustmentPercentageInputValue,
        priceAdjustmentType: values?.priceAdjustmentType,
        resaleAuthorization,
      })

      const isFlexiblePayment = !!values?.flexiblePaymentTerms
      const partnerMargin =
        values?.priceAdjustmentType === CppoPriceAdjustmentType.FIXED
          ? markupOrTotal
          : values?.adjustmentPercentageInputValue
      const dimensions = values?.dimensions?.map(
        ({ price, prices, ...item }) => {
          const resaleDimension = (resaleAuthorization?.dimensions || [])?.find(
            resaleDimension =>
              resaleDimension?.name === item?.name &&
              item?.isAdditionalUsageAllowed ===
                resaleDimension?.isAdditionalUsageAllowed
          )

          return {
            ...item,
            ...(!isFlexiblePayment
              ? {
                  price: calculateBuyerPrice({
                    percentage: !isEmpty(partnerMargin)
                      ? Number(partnerMargin)
                      : 0,
                    wholesalePrice: resaleDimension?.price as number,
                  }),
                  prices: {
                    price1m: calculateBuyerPrice({
                      percentage: !isEmpty(partnerMargin)
                        ? Number(partnerMargin)
                        : 0,
                      wholesalePrice: (resaleDimension?.prices as PriceTypes)
                        .price1m,
                    }),
                    price12m: calculateBuyerPrice({
                      percentage: !isEmpty(partnerMargin)
                        ? Number(partnerMargin)
                        : 0,
                      wholesalePrice: (resaleDimension?.prices as PriceTypes)
                        .price12m,
                    }),
                    price24m: calculateBuyerPrice({
                      percentage: !isEmpty(partnerMargin)
                        ? Number(partnerMargin)
                        : 0,
                      wholesalePrice: (resaleDimension?.prices as PriceTypes)
                        .price24m,
                    }),
                    price36m: calculateBuyerPrice({
                      percentage: !isEmpty(partnerMargin)
                        ? Number(partnerMargin)
                        : 0,
                      wholesalePrice: (resaleDimension?.prices as PriceTypes)
                        .price36m,
                    }),
                    additionalUsagePrice: calculateBuyerPrice({
                      percentage: !isEmpty(partnerMargin)
                        ? Number(partnerMargin)
                        : 0,
                      wholesalePrice: (resaleDimension?.prices as PriceTypes)
                        .additionalUsagePrice,
                    }),
                    subscriptionPrice: calculateBuyerPrice({
                      percentage: !isEmpty(partnerMargin)
                        ? Number(partnerMargin)
                        : 0,
                      wholesalePrice: (resaleDimension?.prices as PriceTypes)
                        .subscriptionPrice,
                    }),
                  },
                }
              : {
                  price,
                  prices: {
                    ...(prices as PriceTypes),
                    additionalUsagePrice: calculateBuyerPrice({
                      percentage: isNonEmptyNumber(partnerMargin as number)
                        ? Number(partnerMargin)
                        : 0,
                      wholesalePrice: (resaleDimension?.prices as PriceTypes)
                        .additionalUsagePrice,
                    }),
                  },
                }),
          }
        }
      )
      await setFieldValue('dimensions', dimensions)
    }
  }

  const handleChangeMarkupPercentageValue = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = (event.target as HTMLInputElement).value
    await setFieldValue(
      'adjustmentPercentageInputValue',
      value === '' ? null : value
    )
  }
  const isApplied =
    isNonEmptyNumber(values?.adjustmentPercentageInputValue) &&
    values?.adjustmentPercentageInputValue === values?.adjustmentPercentage

  const applyDisable =
    !isNonEmptyNumber(values?.adjustmentPercentageInputValue) ||
    !!get(errors, 'adjustmentPercentageInputValue', false)

  const isErrorForValue = !!(
    get(touched, 'adjustmentPercentageInputValue', false) &&
    get(errors, 'adjustmentPercentageInputValue', false)
  )

  const isFlexiblePayment = values?.flexiblePaymentTerms

  const shouldShowMultiSelection =
    productType !== PlanType.SAAS_SUBSCRIPTION &&
    isFlexiblePayment &&
    shouldShowMultiple

  const { totalDueToIsv, margin, markupOrTotal } = getMarkupValues({
    dimensions: values?.dimensions,
    adjustmentPercentage: values?.adjustmentPercentage,
    priceAdjustmentType: values?.priceAdjustmentType,
    resaleAuthorization,
  })

  const handleChangeMarkupType = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = (event.target as HTMLInputElement).value as string

    if (isEmpty(value)) {
      setFieldValue('priceAdjustmentType', values?.priceAdjustmentType)
    } else {
      setFieldValue('priceAdjustmentType', value)
      setFieldValue('adjustmentPercentage', undefined)
      setFieldValue(
        'adjustmentPercentageInputValue',
        value === CppoPriceAdjustmentType.FIXED ? totalDueToIsv : 0
      )
      setFieldTouched('adjustmentPercentageInputValue', false)
      setFieldError('adjustmentPercentageInputValue', '')
      if (discountType === DiscountType.FLEXIBLE_PAYMENT_TERMS) {
        const installmentsData = values?.installments || []
        const updatedInstallments = installmentsData.map(item => ({
          ...item,
          amount: item?.otherAmount,
        }))
        await setFieldValue('installments', updatedInstallments)
      }
    }
  }
  const markupOptions = getMarkupOptions()

  const isMarkupPercentageSelected =
    values?.priceAdjustmentType === CppoPriceAdjustmentType.PERCENTAGE

  return (
    <div data-testid={'markup-section'}>
      <BoxV2
        heading={'Markup'}
        headingIcon={
          <FontAwesomeIcon
            icon={faListCheck}
            style={{ width: '18px', height: '14px' }}
          />
        }
      >
        <div className={classes.markupWrapper}>
          <div className={classes.description}>
            {shouldShowMultiSelection
              ? `Choose how you'd like to input the price increase. The markup
          percentage will be applied to both the total contract value and the
          overage pricing.`
              : 'The markup percentage will be applied to the wholesale pricing. Enter markup percentage below to calculate the buyer price and margin.'}
          </div>
          {shouldShowMultiSelection && (
            <>
              <RadioButtonsV2
                name={'priceAdjustmentType'}
                checkedValue={{
                  key: values.priceAdjustmentType || '',
                  value: values.priceAdjustmentType || '',
                }}
                handleChange={handleChangeMarkupType}
                radioButtonMappings={markupOptions}
                border
                alignSameHeight
              />
              {!!touched?.priceAdjustmentType &&
                errors?.priceAdjustmentType && (
                  <div className={commonClasses.errorMessage}>
                    {errors?.priceAdjustmentType}
                  </div>
                )}
            </>
          )}
          <div className={classes.inputWrapper}>
            <div className={classes.input}>
              <TextInputV2
                keyName="adjustmentPercentageInputValue"
                title={
                  values?.priceAdjustmentType === CppoPriceAdjustmentType.FIXED
                    ? 'Total contract for buyer (Optional)'
                    : 'Markup percentage'
                }
                type="text"
                placeHolder=""
                value={
                  isNonEmptyNumber(values?.adjustmentPercentageInputValue)
                    ? values.adjustmentPercentageInputValue!
                    : ''
                }
                updatedValue={
                  isNonEmptyNumber(values?.adjustmentPercentageInputValue)
                    ? values.adjustmentPercentageInputValue!
                    : ''
                }
                isError={
                  !!(
                    get(touched, 'adjustmentPercentageInputValue', false) &&
                    get(errors, 'adjustmentPercentageInputValue', false)
                  )
                }
                onChange={handleChangeMarkupPercentageValue}
                isReadOnly={false}
                isRequired={false}
                errorText={getErrorMsg('adjustmentPercentageInputValue')}
                showSpinner={false}
                onBlur={handleBlur}
              />
            </div>
            <LabraToolTipV2
              tooltipPlacement="bottom"
              description={
                applyDisable
                  ? get(
                      errors,
                      'adjustmentPercentageInputValue',
                      'Please enter number.'
                    )
                  : ''
              }
            >
              <div>
                <ButtonV2
                  className={clsx(
                    classes.applyButton,
                    isErrorForValue ? classes.disabledApply : ''
                  )}
                  smallVariant
                  styleType="outlined"
                  onClick={applyAdjustmentPercentage}
                  disabled={applyDisable}
                >
                  {isApplied ? 'Applied' : 'Apply'}
                </ButtonV2>
                {!isApplied &&
                  formikProps?.submitCount > 0 &&
                  !applyDisable && (
                    <div className={classes.errorMessage}>
                      Please apply the markup.
                    </div>
                  )}
              </div>
            </LabraToolTipV2>
          </div>
          {(shouldShowMultiSelection || showTcv) && (
            <TotalComponent
              columns={[
                {
                  header: 'Total due to ISV',
                  value: totalDueToIsv
                    ? numberFormatWithPrecision(totalDueToIsv, 9)
                    : '-',
                  isPrice: true,
                },
                {
                  header: !isMarkupPercentageSelected
                    ? 'Markup percentage'
                    : 'Total contract for buyer',

                  isPrice: isMarkupPercentageSelected,
                  value:
                    !isNonEmptyNumber(totalDueToIsv) || markupOrTotal === ''
                      ? '-'
                      : `${
                          isMarkupPercentageSelected
                            ? numberFormatWithPrecision(markupOrTotal, 9)
                            : markupOrTotal
                        }${!isMarkupPercentageSelected ? '%' : ''}`,
                },
                {
                  header: 'Margin',
                  value:
                    margin !== '' && isNonEmptyNumber(totalDueToIsv)
                      ? numberFormatWithPrecision(margin, 9)
                      : '-',
                  isPrice: true,
                },
              ]}
            />
          )}
        </div>
      </BoxV2>
    </div>
  )
}
